import { useEffect } from 'react';
import {
  BrowserRouter,
  Navigate,
  Route,
  Routes,
  useLocation,
  useNavigate,
} from 'react-router-dom';
import { Layout } from './components/Layout.js';
import { ToastContainer } from './components/Toast.js';
import { AuthScreen } from './screens/Auth.js';
import { MachinesScreen } from './screens/Machines.js';
import { MachineDetailScreen, ProjectScreen } from './screens/Project.js';
import { NewSessionScreen } from './screens/NewSession.js';
import { SessionScreen } from './screens/Session.js';
import { InboxScreen } from './screens/Inbox.js';
import { ActiveScreen } from './screens/Active.js';
import { SettingsScreen } from './screens/Settings.js';
import { PlanScreen } from './screens/Plan.js';
import { useAuthStore } from './lib/auth-store.js';
import { ws } from './lib/ws.js';
import { useTabAlert } from './lib/use-tab-alert.js';

function ProtectedRoot() {
  const authenticated = useAuthStore((s) => s.authenticated);
  const location = useLocation();
  useEffect(() => {
    if (authenticated) ws.connect();
    else ws.close();
  }, [authenticated]);

  // Анимация вкладки при новых permission_request
  useTabAlert();

  if (!authenticated) return <Navigate to="/auth" replace state={{ from: location }} />;
  return <Layout />;
}

function AuthRoute() {
  const authenticated = useAuthStore((s) => s.authenticated);
  if (authenticated) return <Navigate to="/machines" replace />;
  return <AuthScreen />;
}

export function App() {
  return (
    <BrowserRouter>
      <ToastContainer />
      <RouterEffects />
      <Routes>
        <Route path="/auth" element={<AuthRoute />} />
        <Route element={<ProtectedRoot />}>
          <Route path="/" element={<Navigate to="/machines" replace />} />
          <Route path="/machines" element={<MachinesScreen />} />
          <Route path="/machines/:machineId" element={<MachineDetailScreen />} />
          <Route path="/projects/:projectId" element={<ProjectScreen />} />
          <Route path="/projects/:projectId/new" element={<NewSessionScreen />} />
          <Route path="/projects/:projectId/plan" element={<PlanScreen />} />
          <Route path="/inbox" element={<InboxScreen />} />
          <Route path="/active" element={<ActiveScreen />} />
          <Route path="/settings" element={<SettingsScreen />} />
        </Route>
        {/* Сессия — отдельный полноэкранный layout без bottom-tabs, чтобы input был sticky. */}
        <Route path="/sessions/:sessionId" element={<SessionRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

function SessionRoute() {
  const authenticated = useAuthStore((s) => s.authenticated);
  useEffect(() => {
    if (authenticated) ws.connect();
  }, [authenticated]);

  // Анимация вкладки при новых permission_request
  useTabAlert();

  if (!authenticated) return <Navigate to="/auth" replace />;
  return <SessionScreen />;
}

/**
 * Низкоуровневые эффекты, которые должны жить внутри BrowserRouter.
 *
 * Навигация из service worker. После клика по пушу SW делает
 * `client.focus()` — но `client.navigate()` в установленной PWA на iOS
 * зачастую игнорируется, и пользователь оставался на той странице, где
 * PWA уже была открыта (обычно — список проектов), а не уходил в сессию
 * с pending permission. Поэтому SW шлёт `{ type: 'navigate', url }`, а
 * мы здесь вызываем React Router `navigate()`. Ничего не перезагружает.
 *
 * Автоматическую перезагрузку (controllerchange + visibilitychange)
 * убрали: на iOS standalone PWA она вкупе с setInterval(update) на
 * 60 сек давала каскад reload'ов и белый экран. Если хочется свежий
 * билд — пользователь убивает PWA в app switcher и открывает заново
 * (cold start подхватывает новый SW штатно).
 */
function RouterEffects() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;
    const handler = (event: MessageEvent) => {
      const data = event.data as { type?: string; url?: string } | undefined;
      if (data?.type === 'navigate' && typeof data.url === 'string') {
        navigate(data.url);
      }
    };
    navigator.serviceWorker.addEventListener('message', handler);
    return () => navigator.serviceWorker.removeEventListener('message', handler);
  }, [navigate]);

  return null;
}
